import * as Schema from "effect/Schema";

import { EnvironmentId, ThreadId } from "./baseSchemas.ts";
import { OrchestrationTimelinePartId } from "./orchestration.ts";
import { ProviderInstanceId } from "./providerInstance.ts";

export const LiveSubagentId = Schema.String.pipe(Schema.brand("LiveSubagentId"));
export type LiveSubagentId = typeof LiveSubagentId.Type;

export const LiveSubagentItemId = Schema.String.pipe(Schema.brand("LiveSubagentItemId"));
export type LiveSubagentItemId = typeof LiveSubagentItemId.Type;

export const LiveSubagentPhase = Schema.Literals([
  "pending",
  "running",
  "waiting",
  "completed",
  "failed",
  "stopped",
]);
export type LiveSubagentPhase = typeof LiveSubagentPhase.Type;

export const LiveSubagentWaitingReason = Schema.Literals(["approval", "input", "provider"]);
export type LiveSubagentWaitingReason = typeof LiveSubagentWaitingReason.Type;

export const SubagentObservationFacets = Schema.Struct({
  roster: Schema.Literal(true),
  lifecycleActivity: Schema.Boolean,
  transcript: Schema.Boolean,
  parentAnchor: Schema.Boolean,
});
export type SubagentObservationFacets = typeof SubagentObservationFacets.Type;

export const LiveSubagentScope = Schema.Struct({
  environmentId: EnvironmentId,
  providerInstanceId: ProviderInstanceId,
  threadId: ThreadId,
});
export type LiveSubagentScope = typeof LiveSubagentScope.Type;

export const LiveSubagent = Schema.Struct({
  id: LiveSubagentId,
  phase: LiveSubagentPhase,
  waitingReason: Schema.optionalKey(LiveSubagentWaitingReason),
  displayName: Schema.optionalKey(Schema.String),
  summary: Schema.optionalKey(Schema.String),
  /** Effective model identity explicitly reported by the Provider for this agent. */
  model: Schema.optionalKey(Schema.String),
  /** Effective Provider-native reasoning effort explicitly reported for this agent. */
  reasoningEffort: Schema.optionalKey(Schema.String),
  parentAgentId: Schema.optionalKey(LiveSubagentId),
  parentTimelinePartId: Schema.optionalKey(OrchestrationTimelinePartId),
  updatedAt: Schema.Number,
});
export type LiveSubagent = typeof LiveSubagent.Type;

export const LiveSubagentTool = Schema.Struct({
  callId: Schema.String,
  name: Schema.String,
  status: Schema.Literals(["running", "completed", "failed"]),
  title: Schema.optionalKey(Schema.String),
  input: Schema.optionalKey(Schema.String),
  output: Schema.optionalKey(Schema.String),
  error: Schema.optionalKey(Schema.String),
});
export type LiveSubagentTool = typeof LiveSubagentTool.Type;

export const LiveSubagentLifecycleActivity = Schema.Struct({
  type: Schema.Literals([
    "started",
    "running",
    "waiting",
    "resumed",
    "completed",
    "failed",
    "stopped",
  ]),
  waitingReason: Schema.optionalKey(LiveSubagentWaitingReason),
  summary: Schema.optionalKey(Schema.String),
});
export type LiveSubagentLifecycleActivity = typeof LiveSubagentLifecycleActivity.Type;

const LiveSubagentActivityItemBase = {
  id: LiveSubagentItemId,
  createdAt: Schema.Number,
  complete: Schema.Boolean,
};

export const LiveSubagentActivityItem = Schema.Union([
  Schema.Struct({
    ...LiveSubagentActivityItemBase,
    kind: Schema.Literal("lifecycle"),
    lifecycle: LiveSubagentLifecycleActivity,
  }),
  Schema.Struct({
    ...LiveSubagentActivityItemBase,
    kind: Schema.Literal("text"),
    content: Schema.String,
  }),
  Schema.Struct({
    ...LiveSubagentActivityItemBase,
    kind: Schema.Literal("reasoning"),
    content: Schema.String,
  }),
  Schema.Struct({
    ...LiveSubagentActivityItemBase,
    kind: Schema.Literal("tool"),
    tool: LiveSubagentTool,
  }),
]);
export type LiveSubagentActivityItem = typeof LiveSubagentActivityItem.Type;

/** @deprecated Use {@link LiveSubagentActivityItem}. */
export const LiveSubagentTimelineItem = LiveSubagentActivityItem;
/** @deprecated Use {@link LiveSubagentActivityItem}. */
export type LiveSubagentTimelineItem = LiveSubagentActivityItem;

export const LiveSubagentRosterInput = Schema.Struct({
  threadId: ThreadId,
});
export type LiveSubagentRosterInput = typeof LiveSubagentRosterInput.Type;

export const LiveSubagentTimelineInput = Schema.Struct({
  threadId: ThreadId,
  agentId: LiveSubagentId,
  observationKey: Schema.String,
});
export type LiveSubagentTimelineInput = typeof LiveSubagentTimelineInput.Type;

export const ObservationUnavailableReason = Schema.Literals([
  "unsupported",
  "runtime-not-live",
  "rejected",
  "timed-out",
  "transport",
  "malformed",
]);
export type ObservationUnavailableReason = typeof ObservationUnavailableReason.Type;

const OwnerBoundObservationUnavailableReason = Schema.Literals([
  "unsupported",
  "rejected",
  "timed-out",
  "transport",
  "malformed",
]);

export const LiveSubagentRosterEvent = Schema.Union([
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("snapshot"),
    scope: LiveSubagentScope,
    observationKey: Schema.String,
    facets: SubagentObservationFacets,
    agents: Schema.Array(LiveSubagent),
  }),
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("upsert"),
    scope: LiveSubagentScope,
    observationKey: Schema.String,
    agent: LiveSubagent,
  }),
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("expired"),
    scope: LiveSubagentScope,
    observationKey: Schema.String,
  }),
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("unavailable"),
    scope: LiveSubagentScope,
    observationKey: Schema.String,
    reason: OwnerBoundObservationUnavailableReason,
  }),
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("unavailable"),
    scope: LiveSubagentScope,
    reason: Schema.Literal("runtime-not-live"),
  }),
]);
export type LiveSubagentRosterEvent = typeof LiveSubagentRosterEvent.Type;

export const LiveSubagentActivityEvent = Schema.Union([
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("snapshot"),
    scope: LiveSubagentScope,
    observationKey: Schema.String,
    agentId: LiveSubagentId,
    items: Schema.Array(LiveSubagentActivityItem),
    truncated: Schema.Boolean,
  }),
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("append"),
    scope: LiveSubagentScope,
    observationKey: Schema.String,
    agentId: LiveSubagentId,
    itemId: LiveSubagentItemId,
    kind: Schema.Literals(["text", "reasoning"]),
    createdAt: Schema.Number,
    delta: Schema.String,
  }),
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("upsert"),
    scope: LiveSubagentScope,
    observationKey: Schema.String,
    agentId: LiveSubagentId,
    item: LiveSubagentActivityItem,
  }),
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("expired"),
    scope: LiveSubagentScope,
    observationKey: Schema.String,
    agentId: LiveSubagentId,
  }),
  Schema.Struct({
    version: Schema.Literal(1),
    type: Schema.Literal("unavailable"),
    scope: LiveSubagentScope,
    observationKey: Schema.String,
    agentId: LiveSubagentId,
    reason: ObservationUnavailableReason,
  }),
]);
export type LiveSubagentActivityEvent = typeof LiveSubagentActivityEvent.Type;

/** @deprecated Transport method name is retained; use {@link LiveSubagentActivityEvent}. */
export const LiveSubagentTimelineEvent = LiveSubagentActivityEvent;
/** @deprecated Transport method name is retained; use {@link LiveSubagentActivityEvent}. */
export type LiveSubagentTimelineEvent = LiveSubagentActivityEvent;
